/**
 * Sub-pass 2B (final closure) — Analyze idempotency-key helper.
 *
 * One logical analyze operation = one Idempotency-Key. Reused for
 * every automatic transport retry, cleared on completion, replaced
 * for the next deliberate user action.
 *
 * A deliberate action = user click / Enter on a ticker (Landing
 * search, Analyze search, retry button, or a direct navigation to
 * ``/analyze/{ticker}`` from a return-path flow).
 *
 * A NON-deliberate action = a component re-render or the same
 * effect firing twice for the same in-flight operation. Those MUST
 * receive the same key.
 *
 * The key is namespaced by the canonical ticker so unrelated
 * concurrent operations don't collide, and persisted in
 * ``sessionStorage`` so the login/signup/broker-survey return-path
 * flow (which unmounts + remounts the tree) preserves it across
 * the same tab.
 */

const STORAGE_PREFIX = "stockaily.analyze.idem.";

function canonical(ticker) {
  return String(ticker || "").trim().toUpperCase();
}

function safeSession() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch (_) { return null; }
}

function newUuid() {
  // Prefer the browser's cryptographic RNG. Fall back to a
  // best-effort v4 built from crypto.getRandomValues so the key
  // remains collision-resistant even in older browsers.
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      const b = new Uint8Array(16);
      crypto.getRandomValues(b);
      b[6] = (b[6] & 0x0f) | 0x40; // v4
      b[8] = (b[8] & 0x3f) | 0x80;
      const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0"));
      return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
    }
  } catch (_) { /* fall through */ }
  // Last-resort fallback — 32 hex chars is still accepted server-side.
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
}

/**
 * Return the key for an active analyze operation on ``ticker``.
 * If none exists yet, create and persist one.
 *
 * ``forceNew=true`` rotates the key — used by an explicit user
 * action (button click / Enter). Without ``forceNew``, subsequent
 * calls within the same logical operation reuse the persisted key.
 */
export function getOrCreateKey(ticker, { forceNew = false } = {}) {
  const tk = canonical(ticker);
  if (!tk) return newUuid();
  const store = safeSession();
  const storageKey = STORAGE_PREFIX + tk;
  if (!forceNew && store) {
    try {
      const existing = store.getItem(storageKey);
      if (existing) return existing;
    } catch (_) { /* noop */ }
  }
  const key = newUuid();
  if (store) {
    try { store.setItem(storageKey, key); } catch (_) { /* storage full */ }
  }
  return key;
}

/** Clear the persisted key for ``ticker`` after successful completion. */
export function clearKey(ticker) {
  const store = safeSession();
  if (!store) return;
  try { store.removeItem(STORAGE_PREFIX + canonical(ticker)); } catch (_) { /* noop */ }
}

/** Test helper: wipe every persisted analyze idempotency key. */
export function __resetForTests() {
  const store = safeSession();
  if (!store) return;
  try {
    const toRemove = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => { try { store.removeItem(k); } catch (_) { /* noop */ } });
  } catch (_) { /* noop */ }
}

export const __STORAGE_PREFIX = STORAGE_PREFIX;

/**
 * Central axios wrapper for POST /api/analyze/{ticker} that
 * implements the client half of the idempotency contract:
 *
 *   * Attaches ``Idempotency-Key`` on every attempt.
 *   * Recognises HTTP 409 ``idempotency_in_progress`` as a
 *     server-side "retry same key shortly" signal and retries with
 *     the SAME key. Honours ``Retry-After`` header + the response
 *     body's ``retry_after_seconds`` hint. Bounded to at most 5
 *     retries with exponential-ish backoff capped at 5 s each.
 *   * Never retries on 409 ``idempotency_conflict`` (bad client-side
 *     key reuse — must be surfaced immediately).
 *   * Never retries on 422 ``invalid_idempotency_key`` (broken
 *     client — never auto-mint a new key here).
 *   * Never retries on any other HTTP or network failure — that is
 *     the pipeline's responsibility.
 */
const MAX_IN_PROGRESS_RETRIES = 5;
const DEFAULT_RETRY_SECONDS = 3;
const MAX_RETRY_SECONDS = 5;

async function _sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

function _retryAfterMs(err) {
  const resp = err?.response;
  const body = resp?.data?.detail || {};
  const bodyHint = Number(body.retry_after_seconds);
  const headerHint = Number(resp?.headers?.["retry-after"] || resp?.headers?.get?.("retry-after"));
  const seconds = Math.max(
    isFinite(bodyHint) && bodyHint > 0 ? bodyHint : 0,
    isFinite(headerHint) && headerHint > 0 ? headerHint : 0,
    DEFAULT_RETRY_SECONDS,
  );
  return Math.min(seconds, MAX_RETRY_SECONDS) * 1000;
}

export async function postAnalyzeWithIdempotency(api, ticker, { idempotencyKey, params } = {}) {
  const key = idempotencyKey || getOrCreateKey(ticker);
  const url = `/analyze/${ticker}`;
  let lastErr;
  for (let attempt = 0; attempt <= MAX_IN_PROGRESS_RETRIES; attempt++) {
    try {
      return await api.post(url, null, {
        timeout: 0,
        headers: { "Idempotency-Key": key },
        ...(params ? { params } : {}),
      });
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      const code = err?.response?.data?.detail?.code;
      if (status === 409 && code === "idempotency_in_progress" && attempt < MAX_IN_PROGRESS_RETRIES) {
        await _sleep(_retryAfterMs(err));
        continue;   // reuse the SAME key
      }
      // Every other outcome — including 422 invalid_idempotency_key and
      // 409 idempotency_conflict — is terminal for this attempt.
      throw err;
    }
  }
  throw lastErr;
}

// Exposed for tests.
export const __MAX_IN_PROGRESS_RETRIES = MAX_IN_PROGRESS_RETRIES;
export const __retryAfterMsForTests = _retryAfterMs;
